import { useAsyncData } from 'nuxt/app';
import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { useGetData } from '~/composables/useGetData';
import type { ExploreCounts } from '~/types/Location';
import type { LaravelPaginatedResponse } from '~/types/plugins/api';

/**
 * @param suburbId When set, scopes both counts to that suburb (e.g. "12
 *   parties" becomes "parties with a candidate in this suburb") instead of
 *   the site-wide totals.
 */
export function useExploreCounts(
  suburbId: MaybeRefOrGetter<string | null> = null,
) {
  const counts = ref<ExploreCounts>({
    partiesRegistered: 0,
    campaignsActive: 0,
  });
  const pending = ref(true);
  const error = ref<unknown>(null);

  const fetchCount = async (
    resource: 'parties' | 'campaigns',
    id: string | null,
  ) => {
    const { execute } = await useGetData<LaravelPaginatedResponse<unknown>>();
    const result = await execute(resource, {
      params: id ? { 'filter[suburb_id]': id, per_page: 1 } : { per_page: 1 },
    });
    return result?.meta?.total ?? 0;
  };

  // Guards against a slower in-flight request for a previous suburb (or the
  // unscoped totals) resolving after a newer one has already landed, which
  // would otherwise overwrite the current counts with stale data — the same
  // race useVotingAreaCounts guards against.
  let requestId = 0;

  const load = async () => {
    const id = toValue(suburbId);
    const currentRequestId = ++requestId;

    pending.value = true;
    error.value = null;

    try {
      const [parties, campaigns] = await Promise.all([
        fetchCount('parties', id),
        fetchCount('campaigns', id),
      ]);
      if (currentRequestId !== requestId) return;
      counts.value = { partiesRegistered: parties, campaignsActive: campaigns };
    } catch (err) {
      if (currentRequestId !== requestId) return;
      error.value = err;
    } finally {
      if (currentRequestId === requestId) {
        pending.value = false;
      }
    }
  };

  // The very first call to `load()` is wrapped in `useAsyncData` below so
  // Nuxt awaits it on the server — this `watch` used to run with
  // `{ immediate: true }` and drive that first call itself, a fire-and-forget
  // invocation Nuxt never waited on, rendering the Explore tile with its
  // zeroed defaults and refetching the same counts again after hydration.
  // Every later suburb change still goes through this plain (non-immediate)
  // watch, exactly as before.
  watch(() => toValue(suburbId), load);

  // `counts`/`pending`/`error` are plain refs local to this composable
  // instance, so they don't survive SSR->client hydration on their own —
  // only `useAsyncData`'s own `data` is transferred in the payload. The
  // fetcher below mutates them via `load()` (as it always has) and also
  // returns a snapshot of the result, which the `watch` beneath seeds back
  // into those same refs on the client, where `load()` itself never actually
  // runs (its result already sits in the payload under this key).
  //
  // Keyed on the initial suburb so the desktop/mobile `ExploreSection`
  // variants (rendered simultaneously, not conditionally) share one fetch —
  // the same trick `useElection`'s `ELECTION_KEY` uses for its two banners.
  const initialSuburbId = toValue(suburbId);
  const { data: initialResult } = useAsyncData<{
    counts: ExploreCounts;
    // A POJO, never the raw error: `error.value` is a real Error instance,
    // and Nuxt's payload serializer (devalue) throws on arbitrary non-POJOs —
    // so returning it here would turn a transient API failure during SSR into
    // a full page render failure instead of a zeroed tile.
    errorMessage: string | null;
  } | null>(
    `explore-counts-${initialSuburbId ?? 'all'}`,
    async () => {
      await load();
      return {
        counts: counts.value,
        errorMessage: error.value instanceof Error ? error.value.message : null,
      };
    },
    { default: () => null },
  );

  watch(
    initialResult,
    (result) => {
      if (!result) return;
      counts.value = result.counts;
      error.value = result.errorMessage ? new Error(result.errorMessage) : null;
      pending.value = false;
    },
    { immediate: true },
  );

  return { counts, pending, error };
}
